import React, {FunctionComponent, useState, Component, MouseEvent} from "react";
import {Modal, Dimmer, Loader, Form, Message, Button, Segment, Table, Icon, Input} from "semantic-ui-react";
import DeleteDialog from "../components/deleteDialog";
import * as Globals from "./globals";
import "./customers.css";
import {calcPagination} from './globals';

interface ICustomer {
    id:             string,
    name:           string,
    label:          string,
    description:    string
}

const newCustomer = (): ICustomer => ({
    id:             "",
    name:           "",
    label:          "",
    description:    ""
});

const CustomerEditDialog: FunctionComponent<Globals.IItemDialogProps<ICustomer>> = ({item, type, onClose}) => {
    const [data, setData] = useState(item);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isValid = data.name.length && data.label.length;

    return (
        <Modal
            open
            closeIcon
            size="tiny"
            icon="edit"
            onClose={_ => onClose(false)}
        >
            <Modal.Header>{`${type === "new" ? "Create" : "Edit"} customer`}</Modal.Header>
            <Modal.Content>
                <Dimmer active={loading} inverted>
                    <Loader>Process customer info</Loader>
                </Dimmer>
                <Form>
                    <Form.Group widths="equal">
                        <Form.Input error={!data.name.length} fluid label="Name" placeholder="name" value={data.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, name: e.target.value})}/>
                        <Form.Input error={!data.label.length} fluid label="Label" placeholder="label" value={data.label} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, label: e.target.value})}/>
                    </Form.Group>
                    <Form.TextArea label="Description" placeholder="Tell about customer..." value={data.description} onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setData({...data, description: e.currentTarget.value})}/>
                </Form>
                <Message negative hidden={error.length === 0}>
                    {error}
                </Message>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    positive
                    disabled={!isValid}
                    icon="save"
                    labelPosition="left"
                    content="Save"
                    onClick={(e: React.MouseEvent) => {
                        setLoading(true);
                        fetch("/customer", {
                            method: type === "new" ? "POST" : "PUT",
                            headers: {'content-type' : 'application/json'},
                            body: JSON.stringify(data)
                        })
                            .then(result => result.ok
                                ? onClose(true)
                                : result.text()
                                    .then(error => setError(error)))
                            .catch(error => setError(error))
                            .finally(() => setLoading(false))
                    }}
                />
            </Modal.Actions>
        </Modal>
    )
}

export default class Customers extends Component<{}, Globals.IItemsListState<ICustomer>> {
    state = {
        loading: false,
        items: [],
        page: 1,
        filter: "",
        order: "name",
        desc: false,
        dialog: null
    }

    componentDidMount() {
        this.updateCustomers();
    }

    updateCustomers() {
        this.setState({loading: true});
        fetch("./customer")
            .then(res => res.ok
                ? res.json().then((json) => this.setState({items: json}))
                : res.text().then(text => alert(text))
            )
            .finally(() => this.setState({loading: false}))
    }

    render() {
        if (this.state.loading)
            return (
                <Dimmer active inverted>
                    <Loader size='massive'>Loading</Loader>
                </Dimmer>
            );

        const pagination = calcPagination(this.state, 9, key => this.setState({page: key}));

        return (
            <Segment basic textAlign='right'>
                {this.state.dialog}

                <Button
                    floated='left'
                    icon="add user"
                    content="Add Customer"
                    onClick={(e: MouseEvent) => this.setState({dialog: <CustomerEditDialog
                            item={newCustomer()}
                            type={Globals.DialogType.new}
                            onClose={(update:boolean) => {
                                this.setState({dialog: null})
                                update && this.updateCustomers();
                            }}
                        />
                    })}
                />

                <Input
                    floated='right'
                    icon={{ name: 'search', circular: true, link: true }}
                    placeholder='Search...'
                    onChange={(e, data) => this.setState({filter: data.value})}
                />

                <Table celled striped selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Name</Table.HeaderCell>
                            <Table.HeaderCell>Label</Table.HeaderCell>
                            <Table.HeaderCell>Description</Table.HeaderCell>
                            <Table.HeaderCell>Action</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {pagination.data.map((customer: ICustomer, idx) =>  <Table.Row
                            key={idx}
                            className='customer-row'
                            onClick={(e: MouseEvent) => this.setState({dialog: <CustomerEditDialog
                                    item={customer}
                                    type={Globals.DialogType.edit}
                                    onClose={(update: boolean) => {
                                        this.setState({dialog: null})
                                        update && this.updateCustomers();
                                    }}
                                />
                            })}
                        >
                            <Table.Cell>{customer.name}</Table.Cell>
                            <Table.Cell>{customer.label}</Table.Cell>
                            <Table.Cell>{customer.description}</Table.Cell>
                            <Table.Cell collapsing textAlign="center" className="customer-action-cell" onClick={(e: MouseEvent) => e.stopPropagation()}>
                                <Icon
                                    link
                                    name="remove user"
                                    onClick={(e: MouseEvent) => {
                                        e.stopPropagation();
                                        this.setState({dialog: <DeleteDialog
                                                id={customer.id}
                                                label={customer.name}
                                                uri="customer"
                                                onClose={(update: boolean) => {
                                                    this.setState({dialog: null})
                                                    update && this.updateCustomers();
                                                }}
                                            />
                                        })
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>

                    {pagination.ctrl}

                </Table>
            </Segment>
        )
    }
}